// The agent console — what someone taking calls looks at all day.
//
// Deliberately NOT the softphone page. That one is a diagnostic instrument:
// presets, raw WSS URLs, provider topologies, things you need to prove a trunk
// works. An agent needs none of it and should never be shown a field that can
// point their phone at the wrong switch. Here there is no configuration at all —
// state, queues, and the people you work with.
import React, { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { Headphones, Coffee, RefreshCw, Users, Clock, PhoneCall, AlertTriangle, Check } from 'lucide-react';
import { agentConsoleService, MySeat, MyState } from 'api/services/agentConsole.service';
import { subscribe, getState, actions } from 'telephony/softphoneStore';

/** Break reasons. Free text would make break reporting unaggregatable. */
const REASONS = ['Break', 'Lunch', 'Meeting', 'Training', 'Admin'];

const AgentConsole: React.FC = () => {
  const phone = useSyncExternalStore(subscribe, getState);
  const [seat, setSeat] = useState<MySeat | null>(null);
  const [hasSoftphone, setHasSoftphone] = useState(false);
  const [softphoneAllowed, setSoftphoneAllowed] = useState(true);
  const [state, setState] = useState<MyState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadSeat = useCallback(async () => {
    try {
      const s = await agentConsoleService.mySeat();
      setSeat(s.seat);
      setHasSoftphone(s.hasSoftphone);
      setSoftphoneAllowed(s.softphoneAllowed);
    } catch { setSeat(null); }
  }, []);

  const loadState = useCallback(async () => {
    setError(null);
    try {
      setState(await agentConsoleService.myState());
    } catch (e: any) {
      // 503 means the switch could not be reached. Showing "Available" then
      // would put an agent back into a queue they believe they left, so the
      // honest render is "unknown".
      setState(null);
      setError(e?.response?.status === 404
        ? 'You have no seat in this organization, so there is nothing to be available for.'
        : e?.response?.status === 503
          ? 'The switch is unreachable, so your availability cannot be confirmed.'
          : 'Could not read your state.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void loadSeat(); void loadState(); }, [loadSeat, loadState]);

  // Poll while the page is open. Queue depth is only useful if it is current,
  // and the switch is the authority — somebody else may have paused this agent.
  useEffect(() => {
    const t = window.setInterval(() => { void loadState(); }, 10000);
    return () => window.clearInterval(t);
  }, [loadState]);

  const setPaused = async (paused: boolean, reason?: string) => {
    setBusy(true);
    setError(null);
    try {
      await agentConsoleService.setPaused(paused, reason);
      await loadState();
    } catch (e: any) {
      // A refused pause must never look like success: an agent who believes
      // they are on a break while still taking calls is the worst outcome here.
      setError(e?.response?.data?.message || 'The switch refused that change — you are still as you were.');
      await loadState();
    } finally { setBusy(false); }
  };

  const paused = state?.paused === true;
  const onCall = phone.phase === 'in-call' || phone.phase === 'ringing' || phone.phase === 'calling';

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  if (!seat) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="rounded-lg bg-white p-8 text-center shadow dark:bg-gray-800">
          <Headphones className="mx-auto mb-3 h-10 w-10 text-gray-400" />
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">No seat assigned</h1>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-600 dark:text-gray-400">
            An agent console needs a seat of your own — the extension calls reach you on.
            An administrator assigns one under Telephony → Extensions.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-800 dark:text-white">
            <Headphones className="h-5 w-5" /> Agent console
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Extension <span className="font-mono">{seat.extension}</span> · {seat.displayName}
            {seat.followMeEnabled && ' · follow-me on'}
          </p>
          {state && (
            // "since login", not "today": Asterisk resets this counter when
            // app_queue reloads, and a total that silently restarts is worse
            // than one that says what it means.
            <p className="mt-0.5 text-xs text-gray-500">
              {state.callsTaken} call{state.callsTaken === 1 ? '' : 's'} taken since login
              {state.lastCallSecondsAgo != null && `, last ${formatAgo(state.lastCallSecondsAgo)}`}
            </p>
          )}
        </div>
        <button onClick={() => void loadState()}
          className="inline-flex items-center gap-1.5 rounded border border-gray-300 px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {!softphoneAllowed && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertTriangle className="mb-0.5 mr-1.5 inline h-4 w-4" />
          The browser softphone needs purchased seats — contact Green Giraffe.
        </div>
      )}

      {softphoneAllowed && !hasSoftphone && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertTriangle className="mb-0.5 mr-1.5 inline h-4 w-4" />
          No browser phone has been issued to you yet. Open the{' '}
          <Link to="/telephony/softphone" className="underline">softphone</Link> once and it will be provisioned.
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Availability. The largest thing on the page, because it is the control
          an agent uses most and the one whose current value they must never be
          in doubt about. */}
      <div className={`mb-5 rounded-xl border p-5 ${paused
        ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20'
        : 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20'}`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className={`text-lg font-semibold ${paused
              ? 'text-amber-800 dark:text-amber-300' : 'text-green-800 dark:text-green-300'}`}>
              {state == null ? 'Unknown' : paused ? `Not ready${state.pauseReason ? ` · ${state.pauseReason}` : ''}` : 'Available'}
            </div>
            <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
              {state == null
                ? 'The switch did not answer, so this may be out of date.'
                : paused
                  ? 'You are not being offered queue calls.'
                  : 'Queue calls can reach you.'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {paused ? (
              <button disabled={busy} onClick={() => void setPaused(false)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50">
                <Check className="h-4 w-4" /> Go available
              </button>
            ) : (
              REASONS.map(r => (
                <button key={r} disabled={busy} onClick={() => void setPaused(true, r)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-amber-400 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50 dark:text-amber-300 dark:hover:bg-amber-900/40">
                  <Coffee className="h-3.5 w-3.5" /> {r}
                </button>
              ))
            )}
          </div>
        </div>
        {/* The gap that "Available" hides. An unregistered device is not rung by
            app_queue, so without this an agent waits all morning and nothing on
            screen explains the silence. */}
        {!paused && state?.deviceState === 'Unavailable' && (
          <p className="mt-3 rounded border border-red-300 bg-red-50 px-2.5 py-1.5 text-xs text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300">
            <AlertTriangle className="mb-0.5 mr-1 inline h-3.5 w-3.5" />
            You are marked available, but your phone is not registered — queue calls
            cannot reach you. Connect the{' '}
            <Link to="/telephony/softphone" className="underline">softphone</Link> to start receiving them.
          </p>
        )}
        {onCall && !paused && (
          // Asterisk does not take a call away when a member pauses; the current
          // one finishes. Saying so stops an agent hanging up to "be safe".
          <p className="mt-3 text-xs text-gray-600 dark:text-gray-400">
            You are on a call. Going not-ready stops the NEXT queue call — it will not drop this one.
          </p>
        )}
      </div>

      {/* Queues. Waiting and longest wait are the two numbers that change what an
          agent does next. */}
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
        <Users className="h-4 w-4" /> My queues
      </h2>
      <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
        {!state || state.queues.length === 0 ? (
          <p className="p-6 text-center text-sm text-gray-500">
            You are not a member of any queue, so no queue calls will reach you.
          </p>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {state.queues.map(q => {
              const others = q.members.filter(m => m.interface !== state.memberInterface);
              const ready = others.filter(m => !m.paused && m.state !== 'Unavailable').length;
              return (
                <li key={q.queue} className="px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{q.queue}</div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className={q.waiting > 0 ? 'font-semibold text-amber-700 dark:text-amber-400' : 'text-gray-500'}>
                        <PhoneCall className="mr-1 inline h-4 w-4" />{q.waiting} waiting
                      </span>
                      {q.longestWaitSeconds > 0 && (
                        <span className="text-amber-700 dark:text-amber-400">
                          <Clock className="mr-1 inline h-4 w-4" />
                          longest {Math.floor(q.longestWaitSeconds / 60)}m {q.longestWaitSeconds % 60}s
                        </span>
                      )}
                      <span className="text-gray-500">{ready} other{ready === 1 ? '' : 's'} ready</span>
                    </div>
                  </div>
                  {others.length > 0 && (
                    <ul className="mt-2 space-y-0.5">
                      {others.map(m => (
                        <li key={m.interface} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <span className={`inline-block h-2 w-2 rounded-full ${
                            m.paused ? 'bg-amber-500' : m.state === 'Unavailable' ? 'bg-gray-400' : 'bg-green-500'}`} />
                          {m.name}
                          <span className="opacity-70">
                            {m.paused ? `not ready${m.pauseReason ? ` · ${m.pauseReason}` : ''}` : m.state.toLowerCase()}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="mt-3 text-xs text-gray-400">
        Availability is read from the switch, not remembered here — if a supervisor changes it,
        this reflects that within ten seconds.
      </p>
    </div>
  );
};

/** "3m ago" / "2h ago" — a raw seconds count is not a thing anyone reads. */
function formatAgo(secs: number) {
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default AgentConsole;
